import React from 'react';
import styled from 'styled-components';

import { Icon } from '../icon/icon';

// ─── Public types ─────────────────────────────────────────────────────────────

type FilterOption = {
  value: string;
  label: string;
  /** Optional group label for visual separation in the panel. */
  group?: string;
};

type OptionsFilterDefinition = {
  key: string;
  label: string;
  type?: 'options';
  placeholder?: never;
  options: FilterOption[];
};

type TextFilterDefinition = {
  key: string;
  label: string;
  type: 'text';
  placeholder?: string;
  options?: never;
};

export type FilterDefinition = OptionsFilterDefinition | TextFilterDefinition;

export type ActiveFilter = {
  key: string;
  value: string;
};

// ─── TableFilterButton ────────────────────────────────────────────────────────

type TableFilterButtonProps = {
  definitions: FilterDefinition[];
  activeFilters: ActiveFilter[];
  onChange: (filters: ActiveFilter[]) => void;
};

export const TableFilterButton = ({
  definitions,
  activeFilters,
  onChange,
}: TableFilterButtonProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  if (definitions.length === 0) {
    return null;
  }

  const handleCategoryClick = (def: FilterDefinition) => {
    const alreadyActive = activeFilters.some((f) => f.key === def.key);
    if (alreadyActive) {
      return;
    }
    if (def.type === 'text') {
      onChange([...activeFilters, { key: def.key, value: '' }]);
    } else {
      const defaultOption = def.options[0];
      if (defaultOption) {
        onChange([...activeFilters, { key: def.key, value: defaultOption.value }]);
      }
    }
    setIsOpen(false);
  };

  return (
    <FilterButtonContainer ref={containerRef}>
      <FilterIconButton
        $active={activeFilters.length > 0}
        $open={isOpen}
        aria-label="Filter"
        onClick={() => setIsOpen((v) => !v)}
      >
        <Icon icon={['fas', 'filter']} size="sm" />
      </FilterIconButton>

      {isOpen && (
        <FilterPanel>
          {definitions.map((def) => {
            const isActive = activeFilters.some((f) => f.key === def.key);
            return (
              <PanelRow $disabled={isActive} key={def.key} onClick={() => handleCategoryClick(def)}>
                {def.label}
              </PanelRow>
            );
          })}
        </FilterPanel>
      )}
    </FilterButtonContainer>
  );
};

// ─── TableFilterChips ─────────────────────────────────────────────────────────

type TableFilterChipsProps = {
  definitions: FilterDefinition[];
  activeFilters: ActiveFilter[];
  onChange: (filters: ActiveFilter[]) => void;
};

export const TableFilterChips = ({
  definitions,
  activeFilters,
  onChange,
}: TableFilterChipsProps) => {
  const [openChipKey, setOpenChipKey] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const prevFilterKeysRef = React.useRef<Set<string>>(new Set(activeFilters.map((f) => f.key)));

  React.useEffect(() => {
    const newTextFilter = activeFilters.find((af) => {
      const def = definitions.find((d) => d.key === af.key);
      return def?.type === 'text' && !prevFilterKeysRef.current.has(af.key);
    });
    prevFilterKeysRef.current = new Set(activeFilters.map((f) => f.key));
    if (newTextFilter) {
      setOpenChipKey(newTextFilter.key);
    }
  }, [activeFilters, definitions]);

  React.useEffect(() => {
    if (!openChipKey) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenChipKey(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openChipKey]);

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <ChipsRow ref={containerRef}>
      {activeFilters.map((af) => {
        const def = definitions.find((d) => d.key === af.key);
        if (!def) {
          return null;
        }
        const isOpen = openChipKey === af.key;

        if (def.type === 'text') {
          return (
            <ChipWrapper key={af.key}>
              <FilterChip $open={isOpen} onClick={() => setOpenChipKey(isOpen ? null : af.key)}>
                <ChipLabel>
                  {def.label}:{' '}
                  {af.value ? (
                    af.value
                  ) : (
                    <ChipPlaceholder>{def.placeholder ?? '…'}</ChipPlaceholder>
                  )}
                </ChipLabel>
                <ChipRemoveButton
                  aria-label="Remove filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(activeFilters.filter((f) => f.key !== af.key));
                    if (openChipKey === af.key) {
                      setOpenChipKey(null);
                    }
                  }}
                >
                  <Icon icon={['fas', 'xmark']} size="xs" />
                </ChipRemoveButton>
              </FilterChip>

              {isOpen && (
                <ChipPanel>
                  <FilterTextInput
                    autoFocus={true}
                    placeholder={def.placeholder}
                    type="text"
                    value={af.value}
                    onChange={(e) => {
                      const next = activeFilters.filter((f) => f.key !== def.key);
                      onChange([...next, { key: def.key, value: e.target.value }]);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setOpenChipKey(null);
                      }
                    }}
                  />
                </ChipPanel>
              )}
            </ChipWrapper>
          );
        }

        const opt = def.options.find((o) => o.value === af.value);
        if (!opt) {
          return null;
        }
        return (
          <ChipWrapper key={af.key}>
            <FilterChip $open={isOpen} onClick={() => setOpenChipKey(isOpen ? null : af.key)}>
              <ChipLabel>
                {def.label}: {opt.label}
              </ChipLabel>
              <ChipRemoveButton
                aria-label="Remove filter"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(activeFilters.filter((f) => f.key !== af.key));
                  if (openChipKey === af.key) {
                    setOpenChipKey(null);
                  }
                }}
              >
                <Icon icon={['fas', 'xmark']} size="xs" />
              </ChipRemoveButton>
            </FilterChip>

            {isOpen && (
              <ChipPanel>
                {def.options.reduce<React.ReactNode[]>((acc, o, i) => {
                  const prevOpt = i > 0 ? def.options[i - 1] : null;
                  if (o.group && o.group !== prevOpt?.group) {
                    acc.push(<PanelGroupHeader key={`g-${o.group}`}>{o.group}</PanelGroupHeader>);
                  }
                  const isSelected = o.value === af.value;
                  acc.push(
                    <PanelRow
                      $selected={isSelected}
                      key={o.value}
                      onClick={() => {
                        const next = activeFilters.filter((f) => f.key !== def.key);
                        onChange([...next, { key: def.key, value: o.value }]);
                        setOpenChipKey(null);
                      }}
                    >
                      {o.label}
                      {isSelected && <Icon icon={['fas', 'check']} size="xs" />}
                    </PanelRow>,
                  );
                  return acc;
                }, [])}
              </ChipPanel>
            )}
          </ChipWrapper>
        );
      })}
    </ChipsRow>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const FilterButtonContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-self: stretch;
`;

const FilterIconButton = styled.button<{ $active: boolean; $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #888;
  border-left: none;
  cursor: pointer;
  padding: 0 0.6em;
  color: #444;
  position: relative;

  ${(p) =>
    p.$open &&
    `
    background-color: #444;
    color: white;
    border-color: #444;
  `}

  &:hover:not(:disabled) {
    ${(p) => !p.$open && 'background-color: #f0f0f0;'}
  }

  &::after {
    content: '';
    display: ${(p) => (p.$active ? 'block' : 'none')};
    position: absolute;
    top: -0.25em;
    right: -0.25em;
    width: 0.5em;
    height: 0.5em;
    border-radius: 50%;
    background-color: ${(p) => p.theme.color.brand.dark};
    box-shadow: 0 0 0 1px #fff;
  }
`;

const FilterPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 200;
  padding: 4px 0;
`;

const PanelRow = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  font-size: 13px;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.4 : 1)};
  background: ${(p) => (p.$selected ? '#f0f0f0' : 'transparent')};
  font-weight: ${(p) => (p.$selected ? 500 : 400)};

  &:hover {
    ${(p) => !p.$disabled && 'background: #f5f5f5;'}
  }
`;

const PanelGroupHeader = styled.div`
  padding: 6px 12px 2px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.375rem;
`;

const ChipWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const FilterChip = styled.div<{ $open?: boolean }>`
  display: inline-flex;
  align-items: stretch;
  background: ${(p) => (p.$open ? '#d2e3fc' : '#e8f0fe')};
  border: 1px solid #c5d4f5;
  border-radius: 100px;
  font-size: 11px;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: #d2e3fc;
  }
`;

const ChipLabel = styled.span`
  padding: 2px 4px 2px 8px;
  white-space: nowrap;
  line-height: 1.4;
`;

const ChipRemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-left: 1px solid #c5d4f5;
  cursor: pointer;
  padding: 2px 6px;
  color: inherit;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
`;

const ChipPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 200;
  padding: 4px 0;
`;

const ChipPlaceholder = styled.span`
  opacity: 0.5;
`;

const FilterTextInput = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 12px;
  font-size: 13px;
  border: none;
  outline: none;
  background: transparent;
`;
